import React, { useState } from "react";
import { useSelector } from "react-redux";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useAmanFlow from "../../../../hooks/useAmanFlow";
import useAmanFormData from "../../../../hooks/useAmanFormData";
import AmanJourneyLayout from "../../layouts/AmanJourneyLayout";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import AmanBenefitsModal from "../../modals/AmanBenefitsModal";
import QuestionnaireReviewModal from "../../modals/QuestionnaireReviewModal";
import YesNoTabs from "../../../common/tabs/yesNoTabs";
import { sendAmanReviewEmail } from "../../../../api/amanServices";
import { selectAmanForm } from "../../../../redux/features/amanFormSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import listIcon from "../../../../assets/images/list-black-1.svg";

const AMAN_QUESTIONNAIRE = "AMAN_QUESTIONNAIRE";

const QuestionnairePage = ({ onClose }) => {
  const translate = useLanguageTranslate();
  const { handleNextSubstep } = useAmanFlow();
  const { saveAmanDataInRedux, redux_form_data } = useAmanFormData(AMAN_QUESTIONNAIRE, () => {});

  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;
  const profileData = useSelector(selectProfileData);
  const amanFormData = useSelector(selectAmanForm);
  const basicInfoData = amanFormData?.AMAN_BASIC_INFO || {};
  const planData = amanFormData?.AMAN_PLAN_SELECTION || {};
  const isAmanPlus = planData?.planId === "aman_plus";

  const [answers, setAnswers] = useState(redux_form_data || {});
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDateOfBirth = (dateValue) => {
    if (!dateValue) return "";
    try {
      const dateString = String(dateValue);
      if (dateString.includes("-")) {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        }
      }
      return dateString;
    } catch {
      return String(dateValue);
    }
  };

  const getUserDetails = () => {
    if (isLoggedIn && profileData) {
      const fullName = profileData?.FirstName && profileData?.LastName
        ? `${profileData.FirstName} ${profileData.LastName}`.trim()
        : profileData?.FullName || profileData?.Name || "";
      
      return {
        name: fullName,
        email: profileData?.Email || "",
        mobile: profileData?.PhoneNumber || profileData?.MobileNumber || "",
        dob: formatDateOfBirth(profileData?.DateOfBirth),
      };
    }
    return {
      name: basicInfoData?.fullName || "",
      email: basicInfoData?.email || "",
      mobile: basicInfoData?.contactNumber || basicInfoData?.mobile || basicInfoData?.mobileNumber || "",
      dob: formatDateOfBirth(basicInfoData?.birthDate || basicInfoData?.dateOfBirth) || basicInfoData?.dateOfBirthFormatted || "",
    };
  };

  const questions = [
    {
      id: "q1",
      question: translate(
        "Heart attack or stroke, or undergone open heart surgery (such as a heart value) or transplantation, or chest pain, or any kind of heart or circulatory problem?",
        "أزمة قلبية أو جلطة ، أو اجراء عملية القلب المفتوح (عملية صمام او الشريان التاجي أو الشريان الأورطي) عملية زراعة عضو ، ألم في الصدر أو عارض صحي آخر مرتبط بأمراض القلب و الدورة الدموية؟"
      ),
    },
    {
      id: "q2",
      question: translate(
        "Hypertension (high blood pressure)",
        "ارتفاع ضغط الدم"
      ),
    },
    {
      id: "q3",
      question: translate(
        "Diabetes (high sugar level)",
        "مرض السكري"
      ),
    },
    {
      id: "q4",
      question: translate(
        "Asthma",
        "أمراض الجهاز التنفسي والربو"
      ),
    },
    {
      id: "q5",
      question: translate(
        "Thyroid Disorders",
        "اضطرابات الغدة الدرقية"
      ),
    },
    {
      id: "q6",
      question: translate(
        "Hyperlipidemia (high cholesterol)",
        "ارتفاع نسبة الكوليسترول"
      ),
    },
  ];

  const amanPlusQuestions = [
    {
      id: "qp1",
      question: translate(
        "Is the domestic helper in good health and free from mental or physical defects?",
        "هل العامل المنزلي بصحة جيدة وخالي من العيوب العقلية أو الجسدية؟"
      ),
      triggerOnNo: true,
    },
    {
      id: "qp2",
      question: translate(
        "Has your domestic helper faced any accidents and illnesses with hospitalisation or had an insurance cover?",
        "هل تعرض العامل المنزلي لأي حوادث أو أمراض استلزمت دخول المستشفى أو كان لديه تغطية تأمينية؟"
      ),
      triggerOnNo: false,
    },
    {
      id: "qp3",
      question: translate(
        "Has any other insurance company ever declined to give you insurance, required special terms, cancelled or refused to renew your insurance?",
        "هل سبق لأي شركة تأمين أخرى أن رفضت منحك التأمين أو طلبت شروطاً خاصة أو ألغت أو رفضت تجديد تأمينك؟"
      ),
      triggerOnNo: false,
    },
    {
      id: "qp4",
      question: translate(
        "Have you ever claimed or received compensation under any accident policy?",
        "هل سبق لك أن قدمت مطالبة أو حصلت على تعويض بموجب أي بوليصة حوادث؟"
      ),
      triggerOnNo: false,
    },
  ];

  const allQuestions = [...questions, ...amanPlusQuestions];

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const allAnswered = allQuestions.every((q) => answers[q.id] !== undefined);

  const needsMedicalReview = () => {
    const baseYes = questions.some((q) => answers[q.id] === true || answers[q.id] === "yes");
    const plusTrigger = amanPlusQuestions.some((q) => {
      const answer = answers[q.id];
      if (q.triggerOnNo) {
        return answer === false || answer === "no";
      }
      return answer === true || answer === "yes";
    });
    return baseYes || plusTrigger;
  };

  const handleContinue = async () => {
    await saveAmanDataInRedux(answers);

    if (needsMedicalReview()) {
      setIsSubmitting(true);
      try {
        const userDetails = getUserDetails();
        const emailPayload = {
          userName: userDetails.name,
          userEmail: userDetails.email,
          userMobile: userDetails.mobile,
          userDob: userDetails.dob,
          q1HeartConditions: answers.q1 === true || answers.q1 === "yes",
          q2HighBloodPressure: answers.q2 === true || answers.q2 === "yes",
          q3Diabetes: answers.q3 === true || answers.q3 === "yes",
          q4RespiratoryDiseases: answers.q4 === true || answers.q4 === "yes",
          q5ThyroidDisorders: answers.q5 === true || answers.q5 === "yes",
          q6HighCholesterol: answers.q6 === true || answers.q6 === "yes",
        };
        emailPayload.qp1GoodHealth = answers.qp1 === true || answers.qp1 === "yes";
        emailPayload.qp2AccidentsHospitalisation = answers.qp2 === true || answers.qp2 === "yes";
        emailPayload.qp3InsuranceDeclined = answers.qp3 === true || answers.qp3 === "yes";
        emailPayload.qp4ClaimedCompensation = answers.qp4 === true || answers.qp4 === "yes";
        await sendAmanReviewEmail(emailPayload);
        setShowReviewModal(true);
      } catch (error) {
        console.error("Failed to send review email:", error);
        setShowReviewModal(true);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      handleNextSubstep();
    }
  };

  return (
    <AmanJourneyLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      onClose={onClose}
    >
      <AmanBenefitsModal
        show={showBenefitsModal}
        onClose={() => setShowBenefitsModal(false)}
      />
      <QuestionnaireReviewModal
        show={showReviewModal}
        onClose={() => setShowReviewModal(false)}
      />

      <BottomStickyBtnLayout
        btn_label={isSubmitting ? translate("Processing...", "جاري المعالجة...") : translate("Continue", "استمرار")}
        btn_onClick={handleContinue}
        btn_disabled={!allAnswered || isSubmitting}
        show_summary={false}
      >
        <div className="w-[500px] max767:w-full max767:px-5 mx-auto ipad:w-[60%]">
          <h1 className="text-[#1A2024] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] text-center max767:text-left max767:rtl:text-right mb-2">
            {translate("Medical Questionnaire for the Domestic Helper", "استبيان الافحاص الطبي للعمالة المنزلية")}
          </h1>
          <p className="text-[#1A2024] text-base not-italic font-normal leading-6 tracking-[-0.16px] text-center mt-1 mb-6 max767:text-left max767:rtl:text-right">
            {translate(
              "Please read all questions carefully and make sure you provide all the relevant information",
              "يرجى قراءة جميع الأسئلة بعناية والتأكد من تقديم جميع المعلومات ذات الصلة:"
            )}
          </p>

          <div className="space-y-6 pb-32">
            {allQuestions.map((q) => (
              <div 
                key={q.id} 
                className="bg-white p-0"
              >
                <p className="text-[#1A2024] text-base font-medium mb-4">
                  {q.question}
                </p>
                <YesNoTabs
                  value={answers[q.id]}
                  onChange={(value) => handleAnswerChange(q.id, value)}
                />
              </div>
            ))}

            <button
              onClick={() => setShowBenefitsModal(true)}
              className="w-full bg-white border border-[#DDE2E4] rounded-[10px] px-4 py-3 flex items-center justify-center gap-2 mt-6"
            >
              <img src={listIcon} alt="" className="w-5 h-5" />
              <span className="text-[#1A2024] text-sm font-semibold leading-6 tracking-[-0.084px]">
                {translate("View full list of benefits & covers", "عرض القائمة الكاملة للفوائد والتغطيات")}
              </span>
            </button>
          </div>
        </div>
      </BottomStickyBtnLayout>
    </AmanJourneyLayout>
  );
};

export default QuestionnairePage;
